/**
 * Os documentos de uma oportunidade: PDF, fotos, vídeos e outros anexos
 * (24/09/2026).
 *
 * ── Onde moram ───────────────────────────────────────────────────────────
 *
 * No armazenamento de arquivos (Cloudflare R2, #715), na pasta da
 * organização; a linha do comercial guarda os dados e a chave. Antes eram
 * anexo no Twenty (24/09) e depois `bytea` no comercial próprio (#699). O
 * contrato não mudou com nenhuma das duas trocas. Ver `docs/armazenamento.md`.
 *
 * ── O link de abrir vale cinco minutos ───────────────────────────────────
 *
 * Como o anexo de conversa: o navegador abre o arquivo por um link assinado,
 * emitido na listagem, que não precisa de sessão e expira. Guardar o link não
 * serve de nada; a listagem emite um novo a cada leitura.
 */

import { z } from 'zod';
import { InstanteSchema } from './valores.js';

export const CategoriaDoDocumentoSchema = z.enum([
  'documento',
  'imagem',
  'video',
  'audio',
  'planilha',
  'apresentacao',
  'compactado',
  'outro',
]);

export type CategoriaDoDocumento = z.infer<typeof CategoriaDoDocumentoSchema>;

export const DocumentoDaOportunidadeSchema = z
  .object({
    /** O id do anexo no CRM: é por ele que se apaga. Referência, nunca id principal. */
    referencia_externa: z.string(),
    nome: z.string(),
    /** Com o ponto: `.pdf`. Nulo quando o CRM não soube dizer. */
    extensao: z.string().nullable(),
    categoria: CategoriaDoDocumentoSchema,
    enviado_em: InstanteSchema,
    /** O link de abrir, assinado. Vale até `url_expira_em`. */
    url: z.string(),
    url_expira_em: InstanteSchema,
  })
  .strict();

export type DocumentoDaOportunidade = z.infer<typeof DocumentoDaOportunidadeSchema>;

export const DocumentosDaOportunidadeSchema = z
  .object({ itens: z.array(DocumentoDaOportunidadeSchema) })
  .strict();

export type DocumentosDaOportunidade = z.infer<typeof DocumentosDaOportunidadeSchema>;

/**
 * O envio é `multipart/form-data`, com os arquivos no campo `arquivos`. Não há
 * campo de texto: o nome é o do próprio arquivo. A schema existe porque o tipo
 * de rota a exige; o servidor confere o multipart.
 */
export const EnviarDocumentosSchema = z.object({}).strict();

/** Até quantos arquivos por envio, e até quantos megabytes cada. */
export const LIMITES_DO_DOCUMENTO = { arquivosPorEnvio: 10, megabytesPorArquivo: 100 } as const;
